import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Popup, Grid } from "semantic-ui-react";
import "../../styles/DocSettings.css";
import Box from "@mui/material/Box";
import {
  fetchDocSettings,
  updateDocSettings,
  fetchDocSettingsSuccess,
  addDocuments,
} from "../../actions/dataConsoleAction";
import CustomToaster from "../../components/CustomToaster";
import Loader from "../../components/Loader";
import PromptLoader from "../../components/PromptLoader";
import SliderComponent from "../../uiComponents/Slider/SliderComponent";
import NumberInput from "../../uiComponents/NumberInputBox/NumberInput";
import DropdownComponent from "../../uiComponents/Dropdown/DropdownComponent";
import TextfieldComponent from "../../uiComponents/TextField/TextfieldComponent";
import "../../uiComponents/Dropdown/DropdownComponent.css";
import "../../uiComponents/Slider/SliderComponent.css";
import "../../uiComponents/NumberInputBox/NumberInput.css";
import "../../uiComponents/TextField/TextFieldComponent.css";
import { set } from "date-fns";

const MAX_CHUNK_SIZE = 2000;
const MIN_CHUNK_SIZE = 0;
const MIN_CHUNK_OVERLAP = 0;

// Load environment variables
const defaultChunkSize = process.env.REACT_APP_CHUNK_SIZE || 100;
const defaultChunk = parseInt(process.env.REACT_APP_CHUNK) || 800;
const defaultChunkOverlap = parseInt(process.env.REACT_APP_CHUNK_OVERLAP);
const defaultDocChunkEnabled = parseInt(process.env.REACT_APP_DOC_AS_CHUNK)|| 0;
const defaultNumList = parseInt(process.env.REACT_APP_MIN_NUM_LIST) || 1;
const defaultEmbeddingModel =
  process.env.REACT_APP_EMBEDDING_MODEL || "text-embedding-ada-002";
const defaultEmbeddingDeployment =
  process.env.REACT_APP_EMBEDDING_DEPLOYMENT || "smart-agent-embedding-ada";
const defaultTrainingModel = process.env.REACT_APP_TRAINING_MODEL || "gpt-4o-mini";
const defaultTrainingMethod =
  process.env.REACT_APP_TRAINING_METHOD || "Multi-modal";
const defaultImageSummarizationPrompt =
  process.env.REACT_APP_IMAGE_SUMMARIZATION_PROMPT || "";
const defaultPageSummaryPrompt =
  process.env.REACT_APP_PAGE_SUMMARY_PROMPT || "";
const defaultDocumentSummaryPrompt =
  process.env.REACT_APP_DOCUMENT_SUMMARY_PROMPT || "";
const defaultCsvColumnsToExtract = process.env.REACT_APP_CSV_COLUMNS_TO_EXTRACT || '';
const defaultServiceAgentEnabled= false ;
const defaultDocAsChunkEnabled= process.env.REACT_APP_DOC_AS_CHUNK_ENABLED || false ;
const defaultPageAsChunkEnabled= true ;

const DocSettings = ({
  isReadOnly,
  datasetId,
  chunkSize,
  chunk,
  chunkOverlap,
  docChunkEnabled,
  numList,
  embeddingModel,
  embeddingDeployment,
  trainingModel,
  trainingMethod,
  imageSummarizationPrompt,
  mapSummaryTemplate,
  reduceSummaryTemplate,
  serviceAgentEnabled,
  docAsChunkEnabled,
  pageAsChunkEnabled,
  setChunkSize,
  setChunkOverlap,
  setChunk,
  setDocChunkEnabled,
  setNumList,
  setEmbeddingModel,
  setEmbeddingDeployment,
  setTrainingModel,
  setTrainingMethod,
  setImageSummarizationPrompt,
  setMapSummaryTemplate,
  setReduceSummaryTemplate,
  setServiceAgentEnabled,
  setDocAsChunkEnabled,
  setPageAsChunkEnabled,
  userType, // Receive the userType prop
}) => {
  const dispatch = useDispatch();
  const [initialValues, setInitialValues] = useState({});
  const [csvColumns, setCSVColumns] = useState(defaultCsvColumnsToExtract);
  const [isReadOnlyState, setIsReadOnlyState] = useState(false); // Define isReadOnlyState
  
  const { loading, docSettings, error, UpdateDocSettingsStatus } = useSelector(
    (state) =>
      state.fetchdocSettings || {
        docSettings: {},
        loading: false,
        error: null,
        UpdateDocSettingsStatus: null,
      }
  );
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  const [toasterVisible, setToasterVisible] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterAppearance, setToasterAppearance] = useState("success");
  const isMounted = useRef(true);
  const [isLoading, setIsLoading] = useState(false);

  const location = window.location;

  const getLoaderType = (location) => {
    if (location.pathname.includes("aiassistant")) {
      return "aiassistent";
    } else if (location.pathname.includes("dataset")) {
      return "dataconsole";
    }
    return "default";
  };

  const getClassName = (baseClass, location) => {
    if (location.pathname.includes("aiassistant")) {
      return ` aiassistant-${baseClass}`;
    } else if (location.pathname.includes("dataset")) {
      return `dataset-${baseClass}`;
    }
    return baseClass;
  };

  const showToaster = (message, appearance) => {
    setToasterVisible(true);
    setToasterMessage(message);
    setToasterAppearance(appearance);
  };
  const closeToaster = () => {
    setToasterVisible(false);
  };

  const handleDropdownChange =
    (setter) =>
    (event, { value }) => {
      setter(value);
    };

  const handleInputChange = (e) => {
    const { value } = e.target;
    setMapSummaryTemplate(value);
  };

  const MIN_CHUNK = parseInt(process.env.REACT_APP_MIN_CHUNK_SIZE);
  const MAX_CHUNK = parseInt(process.env.REACT_APP_MAX_CHUNK_SIZE);

  const MIN_LIST = parseInt(process.env.REACT_APP_MIN_NUM_LIST);
  const MAX_LIST = parseInt(process.env.REACT_APP_MAX_NUM_LIST);

  const getDataSet = async () => {
    try {
      setIsLoading(true);
      await dispatch(fetchDocSettings(datasetId));
      setIsButtonEnabled(false);
      setIsLoading(false);
    } catch (error) {
      console.error("Error in getting DataSet:", error);
      setIsLoading(false);
    }
  };

  // Modified handlers for radio buttons
  const handleServiceAgentChange = () => {
    setServiceAgentEnabled(prevState => !prevState);
  };

  const handleDocAsChunkChange = (event, newValue) => {
    setDocChunkEnabled(newValue);
  };
  
  const handlePageAsChunkChange = () => {
    setPageAsChunkEnabled(prevState => !prevState);
  };

  const handleChunkChange = (event, newValue) => {
    setChunk(newValue);
    const newMaxChunkOverlap = Math.floor(newValue * 0.4);
    setChunkOverlap((prevOverlap) => Math.min(prevOverlap, newMaxChunkOverlap));
  };

  const handleChunkOverlapChange = (event, newValue) => {
    setChunkOverlap(newValue);
  };

  const handleSave = async () => {
    if (!isReadOnly && isButtonEnabled) {
      if (chunk < chunkOverlap) {
        setChunk(chunkOverlap);
        showToaster(
          'The "Characters Size Per Chunk" should not be smaller than the "Overlapping Characters Between Chunks."',
          "error"
        );
        return;
      }

      setIsLoading(true);
      const payload = {
        chunkSize,
        chunk,
        chunkOverlap,
        numList,
        embeddingModel,
        embeddingDeployment,
        trainingModel,
        trainingMethod,
        imageSummarizationPrompt,
        mapSummaryTemplate,
        reduceSummaryTemplate,
        serviceAgentEnabled,
        docAsChunkEnabled,
        pageAsChunkEnabled, // Ensure this value is correctly passed
        docChunkEnabled,
        csvColumns,
      };
      await dispatch(updateDocSettings(payload, datasetId));
      showToaster("Document Settings updated successfully", "success");
      setIsLoading(false);
      dispatch(fetchDocSettings(datasetId));
      setIsLoading(false);
      setIsButtonEnabled(false);
    }
  };

  useEffect(() => {
    if (userType === 'DatasetAdmin') {
      setIsReadOnlyState(true);
    }
  }, [userType]);

  useEffect(() => {
    if (docSettings && docSettings.length > 0) {
      const settings = docSettings[0];

      if (!settings || Object.keys(settings).length === 0) {
        resetFormFields();
        setIsButtonEnabled(false);
      } else if (
        settings.success === false &&
        settings.message.includes("parameter not found")
      ) {
        alert("Parameter not found");
      } else if (settings.chunk) {
        setChunk(settings.chunk ||500);
        setChunkOverlap(settings.chunkOverlap);
        setChunkSize(settings.chunkSize);
        setNumList(settings.numList);
        setEmbeddingModel(settings.embeddingModel || "");
        setEmbeddingDeployment(
          settings.embeddingDeployment || "smart-agent-embedding-ada"
        );
        setTrainingModel(settings.trainingModel || "gpt-4o-mini");
        setTrainingMethod(settings.trainingMethod || "Multi-modal");
        setImageSummarizationPrompt(settings.imageSummarizationPrompt || "");
        setMapSummaryTemplate(settings.mapSummaryTemplate || "");
        setReduceSummaryTemplate(settings.reduceSummaryTemplate || "");
        setCSVColumns(settings.csvColumns || '');
        setServiceAgentEnabled(settings.serviceAgentEnabled || false);
        setPageAsChunkEnabled(settings.pageAsChunkEnabled || false);
        setDocChunkEnabled(settings.docChunkEnabled || defaultDocChunkEnabled);
        setInitialValues({
          chunkSize: settings.chunkSize,
          chunk: settings.chunk,
          chunkOverlap: settings.chunkOverlap,
          numList: settings.numList,
          embeddingModel: settings.embeddingModel,
          embeddingDeployment: settings.embeddingDeployment,
          trainingModel: settings.trainingModel,
          trainingMethod: settings.trainingMethod,
          imageSummarizationPrompt: settings.imageSummarizationPrompt,
          mapSummaryTemplate: settings.mapSummaryTemplate,
          reduceSummaryTemplate: settings.reduceSummaryTemplate,
          serviceAgentEnabled: settings.serviceAgentEnabled,
          docChunkEnabled: settings.docChunkEnabled || defaultDocChunkEnabled,
          pageAsChunkEnabled: settings.pageAsChunkEnabled,
          csvColumns: settings.csvColumns || '',
        });
      }
    } else {
      resetFormFields();
      setIsButtonEnabled(false);
    }
  }, [docSettings]);

  useEffect(() => {
    if (isMounted.current) {
      getDataSet();
      isMounted.current = false;
      dispatch(fetchDocSettingsSuccess([]));
    }
  }, [datasetId, dispatch]);

  useEffect(() => {
    if (UpdateDocSettingsStatus === 200) {
      showToaster("Prompt updated successfully", "success");
      getDataSet(datasetId);
    } else {
      console.error("Error updating prompt:", docSettings);
    }
  }, [UpdateDocSettingsStatus, datasetId]);

  const resetFormFields = () => {
    setChunkSize(defaultChunkSize);
    setChunk(defaultChunk);
    setChunkOverlap(defaultChunkOverlap || 150); // Ensure defaultChunkOverlap is set correctly
    setNumList(defaultNumList);
    setEmbeddingModel(defaultEmbeddingModel);
    setEmbeddingDeployment(defaultEmbeddingDeployment);
    setTrainingModel(defaultTrainingModel);
    setTrainingMethod(defaultTrainingMethod);
    setImageSummarizationPrompt(defaultImageSummarizationPrompt);
    setMapSummaryTemplate(defaultPageSummaryPrompt);
    setReduceSummaryTemplate(defaultDocumentSummaryPrompt);
    setCSVColumns(defaultCsvColumnsToExtract);
    setServiceAgentEnabled(defaultServiceAgentEnabled);
    setDocAsChunkEnabled(defaultDocAsChunkEnabled);
    setPageAsChunkEnabled(true); // Ensure it is reset to true
    setDocChunkEnabled(defaultDocChunkEnabled);
    setIsButtonEnabled(false);
  };

  const handleClear = () => {
    resetFormFields();
  };

  // Modified useEffect to include serviceAgentEnabled and docAsChunkEnabled
  useEffect(() => {
    const isChanged =
      chunkSize !== initialValues.chunkSize ||
      chunk !== initialValues.chunk||
      chunkOverlap !== initialValues.chunkOverlap ||
      numList !== initialValues.numList ||
      embeddingModel !== initialValues.embeddingModel ||
      embeddingDeployment !== initialValues.embeddingDeployment ||
      trainingModel !== initialValues.trainingModel ||
      trainingMethod !== initialValues.trainingMethod ||
      imageSummarizationPrompt !== initialValues.imageSummarizationPrompt ||
      mapSummaryTemplate !== initialValues.mapSummaryTemplate ||
      reduceSummaryTemplate !== initialValues.reduceSummaryTemplate ||
      csvColumns !== initialValues.csvColumns ||
      serviceAgentEnabled !== initialValues.serviceAgentEnabled ||
      docAsChunkEnabled !== initialValues.docAsChunkEnabled ||
      pageAsChunkEnabled !== initialValues.pageAsChunkEnabled ||
      docChunkEnabled !== initialValues.docChunkEnabled;

    const isChunkMethodValid = !(!pageAsChunkEnabled && chunk === 0 && docChunkEnabled === 0);

    setIsButtonEnabled(isChanged && isChunkMethodValid);
  }, [
    chunkSize,
    chunk,
    chunkOverlap,
    numList,
    embeddingModel,
    embeddingDeployment,
    trainingModel,
    trainingMethod,
    imageSummarizationPrompt,
    mapSummaryTemplate,
    reduceSummaryTemplate,
    csvColumns,
    serviceAgentEnabled,
    docAsChunkEnabled,
    pageAsChunkEnabled,
    docChunkEnabled,
    initialValues,
  ]);
  
  return (
    <div className="doc-section">
      {toasterVisible && (
        <CustomToaster
          message={toasterMessage}
          duration={3000}
          onClose={closeToaster}
          position="right-top"
          appearance={toasterAppearance}
        />
      )}
      {isLoading && (
        <div
          style={{ position: "relative", height: "100vh" }}
          className={getClassName("prompt-loader", location)}
        >
          <PromptLoader loaderType={getLoaderType(location)} />
        </div>
      )}
      {!isLoading && (
        <Grid stackable>
          {/* ... (previous grid rows remain unchanged) ... */}
                  
          <Grid.Row columns={2} className="Embbeded-pair">
            <Grid.Column>
              <Popup
                content={
                  "The OpenAI model that helps you to create meaningful vectors out of your document"
                }
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                trigger={<label className="input-label">Embedding Model</label>}
              />
              <DropdownComponent
                selection
                value={embeddingModel}
                onChange={handleDropdownChange(setEmbeddingModel)}
                disabled={isReadOnlyState || isReadOnly}
                options={[
                  {
                    key: "text-embedding-3-large",
                    text: "text-embedding-3-large",
                    value: "text-embedding-3-large",
                  },
                  {
                    key: "text-embedding-3-small",
                    text: "text-embedding-3-small",
                    value: "text-embedding-3-small",
                  },
                  {
                    key: "text-embedding-ada-002",
                    text: "text-embedding-ada-002",
                    value: "text-embedding-ada-002",
                  },
                ]}
                className={getClassName("dropdown-input", location)}
                dataCy="embedding-model"
                icon="dropdown"
              />
            </Grid.Column>
           
          </Grid.Row>
          <Grid.Row columns={2}>
            <Grid.Column>
              <Popup
                content={
                  "The LLM model provided by OpenAI used to train the data and get the semantic relations in vector form"
                }
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                trigger={<label className="input-label">Images/Summarization Training Model</label>}
              />
              <DropdownComponent
                selection
                value={trainingModel}
                onChange={handleDropdownChange(setTrainingModel)}
                options={[
                  {
                    key: "gpt-3.5-turbo",
                    text: "GPT-3.5-turbo",
                    value: "gpt-3.5-turbo",
                  },
                  { key: "gpt-4o", text: "GPT-4o", value: "gpt-4o" },
                  {
                    key: "gpt-4o-mini",
                    text: "GPT-4o-mini",
                    value: "gpt-4o-mini",}
                ]}
                className={getClassName("dropdown-input", location)}
                dataCy="training-model"
                icon="dropdown"
                disabled={isReadOnlyState || isReadOnly}
              />
            </Grid.Column>
            <Grid.Column>
              <Popup
                content={
                  "The method in which the document will be trained by considering image or non-image content"
                }
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                trigger={<label className="input-label">Training Method</label>}
              />
              <DropdownComponent
                selection
                value={trainingMethod}
                onChange={handleDropdownChange(setTrainingMethod)}
                options={[
                  {
                    key: "Advanced textual",
                    text: "Advanced textual",
                    value: "Advanced textual",
                  },
                  {
                    key: "Multi-modal",
                    text: "Multi-modal",
                    value: "Multi-modal",
                  },
                  {
                    key: "Vision based",
                    text: "Vision based",
                    value: "Vision based",
                  },
                ]}
                className={getClassName("dropdown-input", location)}
                dataCy="training-method"
                icon="dropdown"
                disabled={isReadOnlyState || isReadOnly}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Popup
                content={"Specify the CSV columns to extract. Provide a comma-separated list of column names."}
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                trigger={
                  <label className="csv-columns-label">
                    CSV Columns to Extract
                  </label>
                }
              />
              <TextfieldComponent
                className="csv-columns-input"
                dataCy="csv-columns-to-extract"
                value={csvColumns}
                onChange={setCSVColumns}
                placeholder="e.g., column1, column2, column3"
                rows="3"
                disabled={isReadOnlyState || isReadOnly}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Popup
                content={
                  "The prompt used for extracting image content applicable only when the training method is set to Multi-modal / Vision-based"
                }
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                trigger={
                  <label className="image-summarization-label">
                    Image-summarization-prompt
                  </label>
                }
              />
              <TextfieldComponent
                className="image-summarization-input"
                dataCy="image-summarization-prompt"
                value={imageSummarizationPrompt}
                onChange={setImageSummarizationPrompt}
                rows="7"
                disabled={isReadOnlyState || isReadOnly}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Popup
                content={
                  "The prompt used get the  individual page/chunks summary"
                }
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                trigger={
                  <label className="image-summarization-label">
                    Page summary prompt
                  </label>
                }
              />
              <TextfieldComponent
                className="image-summarization-input"
                dataCy="page-summary-prompt"
                value={mapSummaryTemplate}
                onChange={setMapSummaryTemplate}
                rows="7"
                disabled={isReadOnlyState || isReadOnly}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Popup
                content={
                  "The prompt used to get the final combined summary of individual page summaries"
                }
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                trigger={
                  <label className="image-summarization-label">
                    Document summary prompt
                  </label>
                }
              />
              <TextfieldComponent
                className="image-summarization-input"
                dataCy="document-summary-prompt"
                value={reduceSummaryTemplate}
                onChange={setReduceSummaryTemplate}
                rows="7"
                disabled={isReadOnlyState || isReadOnly}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
  <Grid.Column>
    <div className="toggle-container">
      <div className="toggle-item">
        <label className="toggle-label">Service Agent Persona</label>
        <img
          src={serviceAgentEnabled ? "/images/enable.svg" : "/images/disable.svg"}
          alt="Toggle Service Agent Persona"
          onClick={(e) => {
            if (!isReadOnlyState && !isReadOnly) {
              e.stopPropagation();
              handleServiceAgentChange();
            }
          }}
          className={`toggle-icon ${isReadOnlyState || isReadOnly ? "disabled" : ""}`}
          data-cy="service-agent-persona"
          
        />
      </div>
      <div className="toggle-item">
        <Popup
          content={"Each page will be generated as a chunk"}
          inverted
          position="top left"
          style={{ borderRadius: "15px" }}
          trigger={
            <label className="toggle-label">Each Page As Chunk</label>
          }
        />
        <img
          src={pageAsChunkEnabled ? "/images/enable.svg" : "/images/disable.svg"}
          alt="Toggle Page As Chunk"
          onClick={(e) => {
            if (!isReadOnlyState && !isReadOnly) {
              e.stopPropagation();
              handlePageAsChunkChange();
            }
          }}
          className={`toggle-icon ${isReadOnlyState || isReadOnly ? "disabled" : ""}`}
          data-cy="page-as-chunk"
        />
      </div>
    </div>
  </Grid.Column>
</Grid.Row>
<Grid.Row>
            <Grid.Column>
              <Popup
                  content={
                      "Customize the chunk size and overlap for dividing the file into smaller parts."
                  }
                  inverted
                  position="top left"
                  style={{ borderRadius: "15px" }}
                  // trigger={<h4 className="input-label">Chunk Customization</h4>}
                  trigger={<label className="input-label">Chunk Customization</label>}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Popup
                content={
                  "The characters size of each chunk in which the file will be divided"
                }
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                // trigger={<label className="input-label">Chunk Size</label>}
                trigger={<label className="input-label">Characters Size Per Chunk</label>}
                
              />
              <SliderComponent
                min={MIN_CHUNK_SIZE}
                max={MAX_CHUNK_SIZE}
                value={chunk}
                onChange={handleChunkChange} // Updated to use the new handler
                disabled={isReadOnlyState || isReadOnly || pageAsChunkEnabled} // Disable if pageAsChunkEnabled is true
                step={100}
                marks={[{ value: MIN_CHUNK_SIZE }, { value: MAX_CHUNK_SIZE }]}
                className={`${getClassName("slider-input", location)} half-width-slider`} // Added class for half-width
                dataCy="chunk-size-slider"
              />

                <NumberInput
                  value={chunk}
                  min={MIN_CHUNK_SIZE}
                  max={MAX_CHUNK_SIZE}
                  step={100}
                  onChange={(value) => setChunk(value)}
                  className={getClassName('output-box', location)}
                  dataCy="chunk-size-output-box"
                  disabled={isReadOnlyState || isReadOnly || pageAsChunkEnabled}
                />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Popup
                content={
                  "The overlap characters size between chunks in which the file will be divided. Note: Ideally chunk overlap should be 10-20% of the chunk size."
                }
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                // trigger={<label className="input-label">Chunk Overlap</label>}
                trigger={<label className="input-label">Overlapping Characters Between Chunks</label>}
                

              />
              <SliderComponent
                min={MIN_CHUNK_OVERLAP}
                max={Math.floor(chunk * 0.4)}
                value={chunkOverlap}
                onChange={handleChunkOverlapChange}
                disabled={isReadOnlyState || isReadOnly || pageAsChunkEnabled} // Disable if pageAsChunkEnabled is true
                step={1}
                marks={[{ value: MIN_CHUNK_OVERLAP }, { value: Math.floor(chunk * 0.4) }]}
                className={`${getClassName("slider-input", location)} half-width-slider`}
                dataCy="chunk-overlap-slider"
              />
              <NumberInput
                value={chunkOverlap}
                min={MIN_CHUNK_OVERLAP}
                max={Math.floor(chunk * 0.4)}
                step={1}
                onChange={(value) => setChunkOverlap(Math.min(parseInt(value), Math.floor(chunk * 0.4)))}
                className={getClassName('output-box', location)}
                dataCy="chunk-overlap-output-box"
                disabled={isReadOnlyState || isReadOnly || pageAsChunkEnabled}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Popup
                content={"The number of pages to be considered as a single chunk"}
                inverted
                position="top left"
                style={{ borderRadius: "15px" }}
                // trigger={<label className="input-label">Doc As Chunk</label>}
                trigger={<label className="input-label">Max Pages As Chunk</label>}
                
              />
              <SliderComponent
                min={0}
                max={10}
                value={docChunkEnabled}
                onChange={handleDocAsChunkChange}
                disabled={isReadOnlyState || isReadOnly}
                step={1}
                marks={[{ value: 1 }, { value: 10 }]}
                className={getClassName("slider-input", location) }
                dataCy="doc-as-chunk-slider"
              />
              <NumberInput
                value={docChunkEnabled}
                min={1}
                max={10}
                step={1}
                onChange={(value) => setDocChunkEnabled(parseInt(value))}
                className={getClassName('output-box', location)}
                dataCy="doc-as-chunk-output-box"
                disabled={isReadOnlyState || isReadOnly}
              />
              {!pageAsChunkEnabled && chunk === 0 && docChunkEnabled === 0 && (
                <div className="error-message" style={{ color: 'red', marginTop: '10px' }}>
                  Please choose at least one chunk method
                </div>
              )}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Button
                className="reset-button"
                data-cy="clear-button"
                onClick={handleClear}
                disabled={isReadOnlyState || isReadOnly}
              >
                Reset
              </Button>
              <Button
                className="Save-button"
                data-cy="save-button"
                onClick={handleSave}
                disabled={!isButtonEnabled || isReadOnly}
              >
                Save
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      )}
    </div>
  );
};

export default DocSettings;